'use client';

import { useCallback, useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import Dashboard from '../views/Dashboard';
import Usuarios from '../views/Usuarios';
import CrearUsuario from '../views/CrearUsuario';
import Placeholder from '../views/Placeholder';
import LibroRadicador from '../views/LibroRadicador';
import ExpedientesNoIniciados from '../views/ExpedientesNoIniciados';
import BandejaActas from '../views/BandejaActas';
import CasosAprobados from '../views/CasosAprobados';
import Calendario from '../views/Calendario';
import MisExpedientes from '../views/MisExpedientes';
import BandejaDecisiones from '../views/BandejaDecisiones';
import BandejaReparto from '../views/BandejaReparto';
import BandejaJuzgamiento from '../views/BandejaJuzgamiento';
import BandejaSegundaInstancia from '../views/BandejaSegundaInstancia';
import CargaLaboral from '../views/CargaLaboral';
import CentralNotificaciones from '../views/CentralNotificaciones';
import Reportes from '../views/Reportes';
import PlazosContratos from '../views/PlazosContratos';
import ExpedienteDetalle from '../views/ExpedienteDetalle';
import MisActuaciones from '../views/MisActuaciones';
import MiFirma from '../views/MiFirma';
import CambiarMiContrasena from '../views/CambiarMiContrasena';
import { ROLES_PARTE_UI } from '../lib/menu';
import type { Sesion } from './Login';

// Contenedor de la app autenticada: menú + barra + contenido según la vista activa.
// Las vistas aún no construidas caen en el Placeholder "en construcción".
export default function AppShell({
  usuario,
  onLogout,
}: {
  usuario: Sesion;
  onLogout: () => void;
}) {
  // Las partes del portal (disciplinado/defensor) arrancan en "Mis actuaciones",
  // su única vista; el superusuario en "Ver usuarios" (solo gestiona usuarios);
  // el resto en el tablero de inicio.
  const [vista, setVista] = useState(
    ROLES_PARTE_UI.includes(usuario.rol)
      ? 'mis-actuaciones'
      : usuario.rol === 'SUPER_USUARIO'
        ? 'usuarios'
        : 'inicio',
  );
  // Id del expediente seleccionado para abrir el detalle (vista 'expediente').
  const [expedienteId, setExpedienteId] = useState<string | null>(null);
  // Menú lateral en pantallas pequeñas: cajón deslizable (se cierra al navegar).
  const [menuAbierto, setMenuAbierto] = useState(false);

  const navegar = useCallback((v: string) => {
    setVista(v);
    setMenuAbierto(false);
  }, []);

  // Navega al detalle de un expediente: guarda el id y cambia de vista.
  const abrirExpediente = useCallback((id: string) => {
    setExpedienteId(id);
    if (typeof window !== 'undefined') localStorage.setItem('expedienteSeleccionado', id);
    setVista('expediente');
  }, []);

  // Abre el Libro radicador en modo edición de la Etapa 2 (caso devuelto por el especialista).
  const editarEtapa2 = useCallback((id: string) => {
    if (typeof window !== 'undefined') localStorage.setItem('expedienteEditar', id);
    setVista('radicar-queja');
  }, []);

  // Abre la vista de Crear usuario en MODO EDICIÓN (mismo diseño, botón Guardar):
  // handoff one-shot por localStorage, igual que 'expedienteEditar'.
  const editarUsuario = useCallback((id: string) => {
    if (typeof window !== 'undefined') localStorage.setItem('usuarioEditar', id);
    setVista('usuarios-editar');
  }, []);

  function contenido() {
    switch (vista) {
      case 'inicio':
        return <Dashboard usuario={usuario} onNavegar={setVista} />;
      case 'usuarios':
        return <Usuarios usuario={usuario} onNavegar={setVista} onEditar={editarUsuario} />;
      case 'usuarios-crear':
        return <CrearUsuario usuario={usuario} onVolver={() => setVista('usuarios')} onCreado={() => setVista('usuarios')} />;
      case 'usuarios-editar':
        // Misma vista de Crear usuario en modo edición (lee 'usuarioEditar').
        return (
          <CrearUsuario
            usuario={usuario}
            modo="editar"
            onVolver={() => setVista('usuarios')}
            onCreado={() => setVista('usuarios')}
          />
        );
      case 'expedientes-no-iniciados':
        return <ExpedientesNoIniciados onIrARadicar={() => setVista('radicar-queja')} />;
      case 'actas-reparto': // id viejo (avisos/atajos guardados) → cae en «Ver»
      case 'actas-ver':
        return <BandejaActas usuario={usuario} modo="ver" onNavegar={navegar} />;
      case 'actas-reportes':
        return <BandejaActas usuario={usuario} modo="reportes" />;
      case 'casos-aprobados':
        return <CasosAprobados />;
      case 'calendario':
        return <Calendario />;
      case 'radicar-queja':
        return <LibroRadicador onVolverExpediente={abrirExpediente} />;
      case 'mis-expedientes':
        return <MisExpedientes onAbrir={abrirExpediente} onEditarEtapa2={editarEtapa2} />;
      case 'decision-inicial':
        return <BandejaDecisiones onAbrir={abrirExpediente} />;
      case 'reparto-bandeja':
        return <BandejaReparto onAbrir={abrirExpediente} />;
      case 'juzgamiento-bandeja':
        return <BandejaJuzgamiento onAbrir={abrirExpediente} />;
      case 'segunda-bandeja':
        return <BandejaSegundaInstancia onAbrir={abrirExpediente} />;
      case 'reparto-carga':
        return <CargaLaboral />;
      case 'notificaciones-central':
        return <CentralNotificaciones />;
      case 'reportes':
        return <Reportes />;
      // Mismo reporte de plazos de contratos, ahora también bajo Usuarios → Reporte.
      case 'reportes-plazos':
      case 'usuarios-reporte':
        return <PlazosContratos />;
      case 'mis-actuaciones':
        return <MisActuaciones />;
      case 'mi-firma':
        return <MiFirma />;
      case 'mi-contrasena':
        return <CambiarMiContrasena onVolver={() => setVista(ROLES_PARTE_UI.includes(usuario.rol) ? 'mis-actuaciones' : 'inicio')} />;
      case 'expediente':
        return expedienteId ? (
          <ExpedienteDetalle expedienteId={expedienteId} onVolver={() => setVista('mis-expedientes')} onEditarEtapa2={editarEtapa2} />
        ) : (
          <Placeholder vista={vista} />
        );
      default:
        return <Placeholder vista={vista} />;
    }
  }

  // La app autenticada maneja su propio scroll en <main>; el <body> no debe crecer.
  // Bloqueamos su overflow mientras AppShell está montado (login/portal, que sí usan
  // scroll de página con min-h-screen, quedan intactos) para que los portales de Radix
  // (desplegables/diálogos, montados en document.body) no extiendan la página y dejen
  // ese espacio en blanco al final al hacer scroll.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Menú lateral fijo solo en pantallas grandes. */}
      <div className="hidden lg:block">
        <Sidebar usuario={usuario} vista={vista} onNavegar={navegar} />
      </div>

      {/* En pantallas pequeñas: cajón deslizable con el MISMO menú (se abre con
          la hamburguesa del Topbar y se cierra al navegar o tocar el fondo). */}
      {menuAbierto && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMenuAbierto(false)}
            aria-hidden
          />
          <div className="absolute inset-y-0 left-0 w-64 shadow-2xl">
            <Sidebar usuario={usuario} vista={vista} onNavegar={navegar} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar
          usuario={usuario}
          onLogout={onLogout}
          onNavegar={navegar}
          onAbrirExpediente={abrirExpediente}
          onAbrirMenu={() => setMenuAbierto(true)}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">{contenido()}</main>
      </div>
    </div>
  );
}
